import React from 'react';
import {
  ExperimentOutlined,
  WarningFilled,
  EyeInvisibleOutlined,
} from '@ant-design/icons';

import { PlanNode } from '../../../TraineeDashboard/DevelopmentPlan/types';

type Props = {
  node: PlanNode;
  color: string;
  selected: boolean;
  inactive: boolean;
  hasIssue: boolean;
  editable: boolean;
  onNodeMouseDown: (code: string, e: React.MouseEvent) => void;
  onEnter: (code: string) => void;
  onLeave: () => void;
};

const TIER_LABEL: Record<string, string> = {
  foundational: 'Foundational',
  core: 'Core',
  advanced: 'Advanced',
  mastery: 'Mastery',
};

/**
 * Editor-mode node card (studio2-styled, self-contained CSS — does NOT depend on
 * the trainee's developmentPlan.css). Shows the editable-state cues an author cares
 * about: active/inactive, selection, a readiness warning, code badge + game count.
 * Click-vs-drag is decided by the parent canvas on mouseup.
 */
const EditableNodeCard: React.FC<Props> = ({
  node,
  color,
  selected,
  inactive,
  hasIssue,
  editable,
  onNodeMouseDown,
  onEnter,
  onLeave,
}) => (
  <div
    className={[
      'devadmin-node',
      selected ? 'is-selected' : '',
      inactive ? 'is-inactive' : '',
      hasIssue ? 'has-issue' : '',
      editable ? '' : 'is-readonly',
    ]
      .filter(Boolean)
      .join(' ')}
    style={{ ['--node-color' as string]: color }}
    role="button"
    tabIndex={-1}
    onMouseDown={(e) => onNodeMouseDown(node.code, e)}
    onMouseEnter={() => onEnter(node.code)}
    onMouseLeave={onLeave}
  >
    <span className="devadmin-node__head">
      <span className="devadmin-node__tier">
        {TIER_LABEL[node.tier] ?? node.tier}
      </span>
      <span className="devadmin-node__badges">
        {inactive && (
          <EyeInvisibleOutlined title="Inactive (hidden from trainees)" />
        )}
        {hasIssue && (
          <WarningFilled className="devadmin-node__warn" title="Blocks publish" />
        )}
      </span>
    </span>

    <span className="devadmin-node__name">{node.name}</span>

    <span className="devadmin-node__foot">
      <span className="devadmin-node__code">{node.code}</span>
      {node.game_count > 0 && (
        <span className="devadmin-node__games" title="Games practiced here">
          <ExperimentOutlined /> {node.game_count}
        </span>
      )}
    </span>
  </div>
);

export default EditableNodeCard;
